/* eslint-disable @typescript-eslint/no-explicit-any */
import { TimeUnit } from '@/graphql/generated-types'

export function exitToDashboard() {
  if (typeof window === 'undefined') return
  window.location.href = '/'
  return null
}

export function exitToLogin() {
  if (typeof window === 'undefined') return
  window.location.href = '/login'
  return null
}

export function getTimespan(
  end: Date,
  timeUnit: TimeUnit
): {
  startTime: string
  endTime: string
} {
  let start: Date

  switch (timeUnit) {
    case TimeUnit.Minutes:
      start = new Date(end.getTime() - 60 * 1000 * 60 * 3) // 1 hour
      break
    case TimeUnit.Hours:
      start = new Date(end.getTime() - 60 * 60 * 1000 * 24) // 24 hours
      break
    case TimeUnit.Days:
      start = new Date(end.getTime() - 24 * 60 * 60 * 1000 * 7) // 7 days
      break
  }
  return { startTime: start.toISOString(), endTime: end.toISOString() }
}

export function formatIsoToHourMinute(iso: string): string {
  const date = new Date(iso)
  // “sv” locale gives 24-hour zero-padded time; fallback to en-GB if you prefer
  return date.toLocaleTimeString('en-SE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

type RemoveTypename<T> =
  T extends Array<infer U>
    ? Array<RemoveTypename<U>>
    : T extends object
      ? { [K in keyof T as Exclude<K, '__typename'>]: RemoveTypename<T[K]> }
      : T

export function removeTypename<T>(obj: T): RemoveTypename<T> {
  if (Array.isArray(obj)) {
    return obj.map(removeTypename) as RemoveTypename<T>
  } else if (obj && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      if (key !== '__typename') {
        acc[key] = removeTypename((obj as any)[key])
      }
      return acc
    }, {} as any)
  }
  return obj as RemoveTypename<T>
}

// dateFormatter.js

/**
 * Format a date as “today”, “tomorrow”, “yesterday”,
 * a weekday name (Mon-Sun) if within the next 6 days,
 * or a locale-formatted full date for anything else.
 */
export function formatFriendlyDate(
  target: Date,
  locale = 'sv-SE',
  weekdayWithin = 6,
  /** Force the year to appear even when the date is in the current year */
  alwaysShowYear = false
): string {
  const MILLIS_PER_DAY = 86_400_000

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const diffDays = Math.round(
    (truncateToMidnight(target).getTime() - today.getTime()) / MILLIS_PER_DAY
  )

  // yesterday / today / tomorrow
  if (Math.abs(diffDays) <= 1) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
      diffDays,
      'day'
    )
  }

  // weekday for the next `weekdayWithin` days
  if (diffDays > 1 && diffDays <= weekdayWithin) {
    return target.toLocaleDateString(locale, { weekday: 'long' })
  }

  // month-day (optionally plus year)
  const sameYear = target.getFullYear() === today.getFullYear()
  const options: Intl.DateTimeFormatOptions =
    sameYear && !alwaysShowYear
      ? { month: 'short', day: 'numeric' } // “Apr 27”
      : { month: 'short', day: 'numeric', year: 'numeric' } // “Apr 27 2026”

  return target.toLocaleDateString(locale, options)
}

function truncateToMidnight(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}
